/**
 * Shared AI helper — one place for SSE parsing + REST calls.
 * All calls go through the standard `api` axios instance which already
 * attaches the Bearer token from localStorage.
 */
import api from './api';

/**
 * POST /api/ai/job-autofill — one-shot JSON.
 * @param {string} text — the natural-language request.
 * @returns {Promise<object>} parsed fields.
 */
export const aiJobAutofill = async (text) => {
  const res = await api.post('/ai/job-autofill', { text });
  return res.data.fields;
};

export const aiDeliveryInstructions = async (rough_notes, dropoff_address) => {
  const res = await api.post('/ai/delivery-instructions', { rough_notes, dropoff_address });
  return res.data.polished;
};

export const aiDriverAssistant = async (prompt, job_id) => {
  const res = await api.post('/ai/driver/assistant', { prompt, job_id });
  return res.data.templates;
};

/**
 * SSE stream helper for /ai/concierge and /ai/admin/copilot.
 * Uses fetch + ReadableStream because axios can't stream. Bearer token
 * pulled from localStorage to match the api axios instance.
 *
 * @param {string} endpoint — '/ai/concierge' or '/ai/admin/copilot'
 * @param {object} body — { message, session_id? }
 * @param {(chunk: string) => void} onDelta — called with each text chunk
 * @param {(sessionId: string) => void} onSession — called once with the session id
 * @param {(err?: string) => void} onError
 * @returns {Promise<void>} resolves on 'done'
 */
export const aiStream = async (endpoint, body, { onDelta, onSession, onError } = {}) => {
  const token = localStorage.getItem('token');
  const url = `${process.env.REACT_APP_BACKEND_URL}/api${endpoint}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok || !res.body) {
    const errText = await res.text().catch(() => 'stream failed');
    onError?.(errText);
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // Split by SSE event boundary
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || ''; // last piece may be partial
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith('data:')) continue;
      const jsonStr = line.slice(5).trim();
      try {
        const evt = JSON.parse(jsonStr);
        if (evt.type === 'session') onSession?.(evt.session_id);
        else if (evt.type === 'delta') onDelta?.(evt.content || '');
        else if (evt.type === 'error') onError?.(evt.message);
        else if (evt.type === 'done') return;
      } catch {
        // ignore malformed events
      }
    }
  }
};
